import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Chip,
  Autocomplete,
  Grid,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  LocationOn,
  Search,
  MyLocation,
  LocationCity,
  Business,
  LocalShipping,
  Store,
  Factory,
  Home,
  Map,
  Layers,
  Upload,
} from '@mui/icons-material';
import { useForm, Controller, useWatch, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormSection } from '../common/FormSection';
import { useCreateLocation, useUpdateLocation, type LocationCreate } from '../../hooks/useLocations';
import { geocodingService, type GeocodingResult } from '../../services/geocoding';

// Enhanced location schema with clustering and geocoding support
// Business Rule: Either coordinates (lat AND lon) OR address must be provided
const locationSchema = z.object({
  name: z.string().min(1, 'Location name is required').max(255, 'Name must be less than 255 characters'),
  address: z.string().optional(),
  location_lon: z.number().min(-180, 'Longitude must be between -180 and 180').max(180, 'Longitude must be between -180 and 180').optional(),
  location_lat: z.number().min(-90, 'Latitude must be between -90 and 90').max(90, 'Latitude must be between -90 and 90').optional(),
  location_type: z.string().min(1, 'Location type is required'),
  cluster_id: z.number().nullable().optional(),
  cluster_name: z.string().optional(),
  // Spatial features
  use_geocoding: z.boolean().default(false),
  auto_cluster: z.boolean().default(true),
  // Additional metadata
  description: z.string().optional(),
  operating_hours: z.string().optional(),
  contact_info: z.string().optional(),
}).refine((data) => {
  // Business rule: Either both coordinates OR address must be provided
  const hasCoordinates = data.location_lon !== undefined && data.location_lat !== undefined;
  const hasAddress = data.address && data.address.trim().length > 0;
  
  return hasCoordinates || hasAddress;
}, {
  message: 'Either coordinates (latitude and longitude) OR address must be provided',
  path: ['location_lat'], // Show error on lat field
});

type LocationFormData = z.infer<typeof locationSchema>;

interface LocationCluster {
  id: number;
  name: string;
  description?: string;
  centroid_lon?: number;
  centroid_lat?: number;
  location_count?: number;
}

// Real geocoding service using OpenStreetMap Nominatim API

interface LocationFormProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  editingLocation?: any; // For edit mode - existing location data (renamed from location)
  clusters?: LocationCluster[];
  onClusterCreate?: (name: string, description?: string) => Promise<LocationCluster>;
  enableBulkImport?: boolean;
  onSuccess?: () => void; // Callback for successful submission
}

const LocationForm: React.FC<LocationFormProps> = ({ 
  open, 
  onClose, 
  projectId, 
  editingLocation, // Renamed from location to match prop passed from Locations.tsx
  clusters = [],
  onClusterCreate,
  enableBulkImport = false,
  onSuccess,
}) => {
  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation();
  // Unified VRP architecture - get scenario and dataset context
  const { currentScenario, currentDataset } = null /* TODO: Replace with Toolpad navigation */;
  
  const [geocodingResults, setGeocodingResults] = useState<GeocodingResult[]>([]);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [selectedGeoResult, setSelectedGeoResult] = useState<GeocodingResult | null>(null);
  const [newClusterName, setNewClusterName] = useState('');
  const [showNewClusterInput, setShowNewClusterInput] = useState(false);
  const [availableClusters, setAvailableClusters] = useState<LocationCluster[]>(clusters);

  // Context validation
  const hasValidContext = projectId && currentScenario && currentDataset;
  
  // Determine if we're in edit mode
  const isEditMode = !!editingLocation;
  
  // Debug logging for edit mode
  console.log('🔍 [LocationForm] Component state:', {
    isEditMode,
    editingLocationId: editingLocation?.id,
    editingLocationName: editingLocation?.name,
    projectId,
    formOpen: open
  });
  
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid }
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationSchema),
    mode: 'onChange', // Enable real-time validation
    defaultValues: {
      name: editingLocation?.name || '',
      address: editingLocation?.address || '',
      location_lon: editingLocation?.location_lon,
      location_lat: editingLocation?.location_lat,
      location_type: editingLocation?.location_type || 'customer',
      cluster_id: editingLocation?.cluster_id,
      cluster_name: '',
      use_geocoding: false,
      auto_cluster: true,
      description: editingLocation?.description || '',
      operating_hours: editingLocation?.operating_hours || '',
      contact_info: editingLocation?.contact_info || '',
    },
  });

  // Reset form when editingLocation changes
  useEffect(() => {
    if (editingLocation && open) {
      console.log('🔄 [LocationForm] Resetting form with editing location data:', editingLocation);
      reset({
        name: editingLocation.name || '',
        address: editingLocation.address || '',
        location_lon: editingLocation.location_lon,
        location_lat: editingLocation.location_lat,
        location_type: editingLocation.location_type || 'customer',
        cluster_id: editingLocation.cluster_id,
        cluster_name: '',
        use_geocoding: false,
        auto_cluster: true,
        description: editingLocation.description || '',
        operating_hours: editingLocation.operating_hours || '',
        contact_info: editingLocation.contact_info || '',
      });
    } else if (!editingLocation && open) {
      // Reset to empty form for "add new" mode
      console.log('🆕 [LocationForm] Resetting form for new location');
      reset({
        name: '',
        address: '',
        location_lon: undefined,
        location_lat: undefined,
        location_type: 'customer',
        cluster_id: null,
        cluster_name: '',
        use_geocoding: false,
        auto_cluster: true,
        description: '',
        operating_hours: '',
        contact_info: '',
      });
    }
  }, [editingLocation, open, reset]);

  // Watch for changes to trigger geocoding
  const watchedAddress = useWatch({ control, name: 'address' });
  const watchedUseGeocoding = useWatch({ control, name: 'use_geocoding' });
  const watchedLat = useWatch({ control, name: 'location_lat' });
  const watchedLon = useWatch({ control, name: 'location_lon' });

  // Real geocoding functionality
  const handleGeocode = useCallback(async (address: string) => {
    if (!address || !watchedUseGeocoding) return;
    
    setGeocodingLoading(true);
    setGeocodingError(null);
    try {
      console.log('🌍 [LocationForm] Starting real geocoding for address:', address);
      const results = await geocodingService.geocode(address);
      console.log('📍 [LocationForm] Geocoding results:', results);
      
      if (results.length === 0) {
        setGeocodingError('No results found for this address. Please try a more specific address.');
        setGeocodingResults([]);
        return;
      }
      
      setGeocodingResults(results);
      setGeocodingError(null);
      
      // Auto-select first result if high confidence
      if (results.length > 0 && results[0].confidence > 0.5) {
        setSelectedGeoResult(results[0]);
        setValue('location_lat', results[0].lat);
        setValue('location_lon', results[0].lon);
        console.log('✅ [LocationForm] Auto-selected high confidence result:', {
          address: results[0].address,
          coordinates: `${results[0].lat}, ${results[0].lon}`,
          confidence: results[0].confidence
        });
      }
    } catch (error) {
      console.error('❌ [LocationForm] Geocoding failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Geocoding service unavailable';
      setGeocodingError(errorMessage);
      setGeocodingResults([]);
    } finally {
      setGeocodingLoading(false);
    }
  }, [setValue, watchedUseGeocoding]);

  // Auto-geocode when address changes
  useEffect(() => {
    if (watchedAddress && watchedUseGeocoding) {
      const timeoutId = setTimeout(() => {
        handleGeocode(watchedAddress);
      }, 1000); // Debounce for 1 second
      
      return () => clearTimeout(timeoutId);
    }
  }, [watchedAddress, watchedUseGeocoding, handleGeocode]);

  // Get current location
  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setValue('location_lat', position.coords.latitude);
          setValue('location_lon', position.coords.longitude);
          
          // Reverse geocode if geocoding is enabled
          if (watchedUseGeocoding) {
            geocodingService.reverseGeocode(
              position.coords.latitude,
              position.coords.longitude
            ).then((result) => {
              setValue('address', result.address);
              console.log('🗺️ [LocationForm] Reverse geocoded current location:', result.address);
            }).catch((error) => {
              console.error('❌ [LocationForm] Reverse geocoding failed:', error);
            });
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
        }
      );
    }
  };

  // Handle new cluster creation
  const handleCreateCluster = async () => {
    if (!newClusterName.trim() || !onClusterCreate) return;
    
    try {
      const newCluster = await onClusterCreate(newClusterName.trim());
      setAvailableClusters([...availableClusters, newCluster]);
      setValue('cluster_id', newCluster.id);
      setNewClusterName('');
      setShowNewClusterInput(false);
    } catch (error) {
      console.error('Failed to create cluster:', error);
    }
  };

  // Get location type icon
  const getLocationTypeIcon = (type: string) => {
    switch (type) {
      case 'depot': return <Factory />;
      case 'warehouse': return <Business />;
      case 'customer': return <Home />;
      case 'service_point': return <Store />;
      case 'pickup_point': return <LocalShipping />;
      case 'delivery_point': return <LocationCity />;
      default: return <LocationOn />;
    }
  };
  const onSubmit: SubmitHandler<LocationFormData> = async (data: LocationFormData) => {
    try {
      console.log(`📍 [LocationForm] ${isEditMode ? 'Updating' : 'Submitting'} location data:`, {
        name: data.name,
        projectId,
        location_type: data.location_type,
        coordinates: data.location_lon && data.location_lat ? `${data.location_lat}, ${data.location_lon}` : 'none',
        isEditMode,
        editingLocationId: editingLocation?.id,
        formData: data
      });

      if (isEditMode) {
        console.log('🔄 [LocationForm] Starting location update...');
        
        // Update existing location
        const locationUpdates = {
          name: data.name,
          address: data.address,
          location_lon: data.location_lon,
          location_lat: data.location_lat,
          location_type: data.location_type,
          cluster_id: data.cluster_id || undefined,
        };
        
        console.log('📝 [LocationForm] Location updates to send:', locationUpdates);
        
        const result = await updateLocation.mutateAsync({
          id: editingLocation.id,
          projectId,
          updates: locationUpdates
        });
        
        console.log('✅ [LocationForm] Location updated successfully:', result);
        
        reset();
        onClose();
        onSuccess?.();
      } else {
        const locationData: LocationCreate = {
          name: data.name,
          address: data.address,
          location_lon: data.location_lon,
          location_lat: data.location_lat,
          location_type: data.location_type,
          cluster_id: data.cluster_id || undefined,
          project_id: projectId,
          scenario_id: typeof currentScenario?.id === 'number' ? currentScenario.id : (() => {
            console.error('❌ [LocationForm] Invalid scenario context:', currentScenario);
            throw new Error('Invalid scenario context: scenario ID is not a number');
          })(),
          dataset_id: typeof currentDataset?.id === 'number' ? currentDataset.id : (() => {
            console.error('❌ [LocationForm] Invalid dataset context:', currentDataset);
            throw new Error('Invalid dataset context: dataset ID is not a number');
          })(),
        };
        
        const result = await createLocation.mutateAsync(locationData);
        console.log('✅ [LocationForm] Location created successfully:', result);
        
        reset();
        onClose();
        onSuccess?.();
        
        // Show success message
        console.log('🎉 Location created successfully!');
      }
    } catch (error) {
      console.error(`❌ [LocationForm] Failed to ${isEditMode ? 'update' : 'create'} location:`, error);
    }
  };

  const handleClose = () => {
    reset();
    setGeocodingResults([]);
    setGeocodingError(null);
    setSelectedGeoResult(null);
    setNewClusterName('');
    setShowNewClusterInput(false);
    onClose();
  };

  // Form error handler for debugging
  const onFormError = (errors: any) => {
    console.error('🚨 [LocationForm] Form validation errors:', errors);
    console.error('🔍 [LocationForm] Detailed validation errors:', {
      name: errors.name?.message,
      location_lat: errors.location_lat?.message,
      location_lon: errors.location_lon?.message,
      address: errors.address?.message,
      location_type: errors.location_type?.message,
    });
    
    // Also log the current form values to see what's causing the issue
    const formValues = watch();
    console.log('📋 [LocationForm] Current form values:', {
      name: formValues.name,
      address: formValues.address,
      coordinates: `${formValues.location_lat || 'none'}, ${formValues.location_lon || 'none'}`,
      location_type: formValues.location_type,
      hasValidContext,
      isValid
    });
  };

  // Button click handler for debugging
  const handleSubmitButtonClick = () => {
    console.log('🔘 [LocationForm] Submit button clicked');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocationOn color="primary" />
        {isEditMode ? `Edit Location: ${editingLocation?.name}` : 'Add New Location'}
        {enableBulkImport && !isEditMode && (
          <Tooltip title="Bulk import locations from CSV/Excel">
            <IconButton size="small" sx={{ ml: 'auto' }}>
              <Upload />
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit, onFormError)}>
        <DialogContent sx={{ pb: 2 }}>
          {!hasValidContext && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="600" gutterBottom>
                Missing Context: Cannot create location
              </Typography>
              <Typography variant="caption" display="block">
                Please select a scenario and dataset first:
                {!currentScenario && " • No scenario selected"}
                {!currentDataset && " • No dataset selected"}
              </Typography>
              <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                Use the breadcrumbs above to navigate to Project → Scenario → Dataset first.
              </Typography>
            </Alert>
          )}
          
          {(createLocation.error || updateLocation.error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="600" gutterBottom>
                {isEditMode ? 'Update Failed' : 'Creation Failed'}
              </Typography>
              <Typography variant="body2">
                {createLocation.error instanceof Error 
                  ? createLocation.error.message 
                  : updateLocation.error instanceof Error 
                    ? updateLocation.error.message 
                    : `Failed to ${isEditMode ? 'update' : 'create'} location. Please check all required fields.`}
              </Typography>
              {(!isEditMode && (!watchedLat || !watchedLon) && (!watchedAddress || watchedAddress.trim().length === 0)) && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  Make sure you provide either coordinates (latitude and longitude) or an address.
                </Typography>
              )}
            </Alert>
          )}
          
          {/* Basic Information Section */}
          <FormSection title="Basic Information" required>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Location Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="e.g., Main Warehouse, Customer Site A"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="location_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth required error={!!errors.location_type}>
                      <InputLabel>Location Type</InputLabel>
                      <Select 
                        {...field} 
                        label="Location Type"
                        startAdornment={getLocationTypeIcon(field.value || '')}
                        sx={{ pl: 1 }}
                      >
                        <MenuItem value="depot">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Factory /> Depot
                          </Box>
                        </MenuItem>
                        <MenuItem value="warehouse">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business /> Warehouse
                          </Box>
                        </MenuItem>
                        <MenuItem value="customer">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Home /> Customer
                          </Box>
                        </MenuItem>
                        <MenuItem value="service_point">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Store /> Service Point
                          </Box>
                        </MenuItem>
                        <MenuItem value="pickup_point">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalShipping /> Pickup Point
                          </Box>
                        </MenuItem>
                        <MenuItem value="delivery_point">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationCity /> Delivery Point
                          </Box>
                        </MenuItem>
                      </Select>
                      {errors.location_type && (
                        <FormHelperText>{errors.location_type.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description (Optional)"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Additional information about this location..."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Location Coordinates Section */}
          <FormSection title="Location Coordinates" subtitle="Specify exact coordinates or use address geocoding" required>
            <Grid container spacing={3}>
              
              {/* Requirement Alert */}
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="600" gutterBottom>
                    Coordinate Requirement
                  </Typography>
                  <Typography variant="body2">
                    You must provide either <strong>both latitude and longitude coordinates</strong> OR an <strong>address</strong>. 
                    Use the geocoding feature below to automatically convert addresses to coordinates.
                  </Typography>
                </Alert>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="use_geocoding"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Use Geocoding (Auto-convert address to coordinates)"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Address"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="e.g., 123 Main Street, Johannesburg, South Africa"
                      helperText={
                        watchedUseGeocoding 
                          ? "Enter a complete address including street, city, and country for best results (Required if no coordinates)" 
                          : "Required if no coordinates provided"
                      }
                      InputProps={{
                        startAdornment: <Search />,
                        endAdornment: geocodingLoading ? <CircularProgress size={20} /> : null,
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Geocoding Error */}
              {geocodingError && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Geocoding Error:</strong> {geocodingError}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Try entering a more complete address including city, state/province, and country.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {/* Geocoding Results */}
              {geocodingResults.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Card elevation={2}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        📍 Found {geocodingResults.length} location{geocodingResults.length > 1 ? 's' : ''} - Select the correct one:
                      </Typography>
                      {geocodingResults.map((result, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Button
                            variant={selectedGeoResult === result ? "contained" : "outlined"}
                            onClick={() => {
                              setSelectedGeoResult(result);
                              setValue('location_lat', result.lat);
                              setValue('location_lon', result.lon);
                              console.log('📌 [LocationForm] Selected geocoding result:', {
                                address: result.address,
                                coordinates: `${result.lat}, ${result.lon}`,
                                confidence: result.confidence
                              });
                            }}
                            size="small"
                            fullWidth
                            sx={{ 
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              p: 2
                            }}
                          >
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {result.address}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {result.lat.toFixed(6)}, {result.lon.toFixed(6)} • Confidence: {(result.confidence * 100).toFixed(0)}%
                              </Typography>
                              {result.components.city && result.components.country && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {result.components.city}, {result.components.country}
                                </Typography>
                              )}
                            </Box>
                          </Button>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="subtitle2">Manual Coordinates</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<MyLocation />}
                    onClick={handleGetCurrentLocation}
                  >
                    Use Current Location
                  </Button>
                </Box>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Controller
                  name="location_lat"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value || ''}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        onChange(isNaN(val) ? undefined : val);
                      }}
                      label="Latitude *"
                      type="number"
                      fullWidth
                      error={!!errors.location_lat}
                      helperText={errors.location_lat?.message || 'Range: -90 to 90 (Required if no address)'}
                      inputProps={{ 
                        min: -90, 
                        max: 90, 
                        step: 'any',
                        placeholder: 'e.g., -26.2041'
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Controller
                  name="location_lon"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value || ''}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        onChange(isNaN(val) ? undefined : val);
                      }}
                      label="Longitude *"
                      type="number"
                      fullWidth
                      error={!!errors.location_lon}
                      helperText={errors.location_lon?.message || 'Range: -180 to 180 (Required if no address)'}
                      inputProps={{ 
                        min: -180, 
                        max: 180, 
                        step: 'any',
                        placeholder: 'e.g., 28.0473'
                      }}
                    />
                  )}
                />
              </Grid>

              {(watchedLat && watchedLon) && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success" icon={<Map />}>
                    <strong>Coordinates Set:</strong> Location will be placed at {watchedLat.toFixed(6)}, {watchedLon.toFixed(6)}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </FormSection>

          {/* Clustering Section */}
          <FormSection title="Location Clustering" subtitle="Organize locations into groups for optimization">
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="auto_cluster"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Auto-assign to cluster based on proximity"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="cluster_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={availableClusters}
                      getOptionLabel={(option) => option.name}
                      value={availableClusters.find(c => c.id === field.value) || null}
                      onChange={(_, value) => {
                        // Explicitly handle undefined/null values
                        const clusterId = value?.id || null;
                        console.log('🔄 [LocationForm] Cluster selection changed:', { value, clusterId });
                        field.onChange(clusterId);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Location Cluster (Optional)"
                          placeholder="Select or create a cluster"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <Layers />,
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Layers fontSize="small" />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2">{option.name}</Typography>
                              {option.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.description}
                                </Typography>
                              )}
                            </Box>
                            {option.location_count && (
                              <Chip size="small" label={`${option.location_count} locations`} />
                            )}
                          </Box>
                        </Box>
                      )}
                      freeSolo={false}
                    />
                  )}
                />
              </Grid>

              {/* Create New Cluster */}
              <Grid size={{ xs: 12 }}>
                {!showNewClusterInput ? (
                  <Button
                    variant="outlined"
                    startIcon={<Layers />}
                    onClick={() => setShowNewClusterInput(true)}
                    size="small"
                  >
                    Create New Cluster
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      value={newClusterName}
                      onChange={(e) => setNewClusterName(e.target.value)}
                      label="New Cluster Name"
                      size="small"
                      fullWidth
                      placeholder="e.g., Downtown Area, Industrial Zone"
                    />
                    <Button
                      variant="contained"
                      onClick={handleCreateCluster}
                      disabled={!newClusterName.trim()}
                      size="small"
                    >
                      Create
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowNewClusterInput(false);
                        setNewClusterName('');
                      }}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </FormSection>

          {/* Additional Information Section */}
          <FormSection title="Additional Information" defaultExpanded={false}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="operating_hours"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Operating Hours (Optional)"
                      fullWidth
                      placeholder="e.g., Mon-Fri 9:00-17:00"
                      helperText="Specify when this location is available for service"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="contact_info"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Contact Information (Optional)"
                      fullWidth
                      placeholder="Phone, email, or contact person"
                      helperText="Useful for delivery coordination"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Bulk Import Hint */}
          {enableBulkImport && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<Upload />}>
              <Typography variant="body2">
                <strong>Need to add multiple locations?</strong> Use the bulk import feature to upload locations from CSV or Excel files.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={!hasValidContext || !isValid || isSubmitting || createLocation.isPending || updateLocation.isPending}
            onClick={handleSubmitButtonClick}
            sx={{ minWidth: 120 }}
          >
            {(isSubmitting || createLocation.isPending || updateLocation.isPending) ? 
              <CircularProgress size={20} /> : 
              (isEditMode ? 'Update Location' : 'Add Location')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export { LocationForm };
export default LocationForm;
